/**
 * Optimizer Control Center — per-row action buttons + confirm dialogs
 * For Media Devoted | Created By Ciro Autiero
 */

import { useState, type FC } from 'react';
import { Pause, Pencil, Play, Trash2, Zap } from 'lucide-react';

import { Button, Switch } from '@/components/ui';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { heldNote } from '@/features/optimizer-center/tracker-status/held-copy';
import { formatIntervalMin } from '@/features/optimizer-center/utils/next-run-info';
import { useTrackerBlockedReason } from '@/features/trackers/flow-optimizer';
import { useAuth } from '@/hooks';

import { ConfirmActionDialog } from './ConfirmActionDialog';
import { RestoreSnapshotAction } from './RestoreSnapshotAction';
import { UpdateNowDialog } from './UpdateNowDialog';
import { useRowActions } from './useRowActions';

type PendingConfirm = 'apply' | 'enable' | 'remove' | null;

/** The auto-run confirm; says so when the tracker's own switch holds the scheduler. */
const autoRunText = (row: OptimizerRow, held: string | null): string =>
  `Enable auto-run for "${row.campaignName}"? The scheduler will push weights unattended ` +
  `every ${formatIntervalMin(row.intervalMin)}.${heldNote(held)}`;

export const RowActions: FC<{ row: OptimizerRow; onEdit: (row: OptimizerRow) => void }> = ({
  row,
  onEdit,
}) => {
  const { hasPermission } = useAuth();
  const { updateNow, toggleKillSwitch, setAutoRun, remove } = useRowActions(row);
  const held = useTrackerBlockedReason(row.provider);
  const [confirm, setConfirm] = useState<PendingConfirm>(null);

  const isStandard = row.mode === 'STANDARD';
  const canApply = hasPermission('flows.edit_weights');
  const canManage = hasPermission('flows.edit_settings');
  const canRestore = hasPermission('flows.snapshot_restore');
  const closeConfirm = () => setConfirm(null);
  const settled = { onSettled: closeConfirm };

  const dialogs: Record<NonNullable<PendingConfirm>, JSX.Element> = {
    apply: (
      <UpdateNowDialog
        row={row}
        isBusy={updateNow.isPending}
        onConfirm={(choice) => updateNow.mutate(choice, settled)}
        onCancel={closeConfirm}
      />
    ),
    enable: (
      <ConfirmActionDialog
        title="Enable auto-run"
        description={autoRunText(row, held)}
        confirmLabel="Enable auto-run"
        isBusy={setAutoRun.isPending}
        onConfirm={() => setAutoRun.mutate(true, settled)}
        onCancel={closeConfirm}
      />
    ),
    remove: (
      <ConfirmActionDialog
        title="Remove optimizer config"
        description={`Remove the optimizer configuration for "${row.campaignName}"? The flow keeps its current weights, but TrafficPulse stops managing them.`}
        confirmLabel="Remove"
        destructive
        isBusy={remove.isPending}
        onConfirm={() => remove.mutate(undefined, settled)}
        onCancel={closeConfirm}
      />
    ),
  };

  return (
    <div className="flex items-center justify-end gap-1">
      {canApply && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label="Update now"
          title="Update now"
          disabled={isStandard || row.killSwitch || updateNow.isPending}
          onClick={() => setConfirm('apply')}
        >
          <Zap className="h-4 w-4" />
        </Button>
      )}
      {canRestore && <RestoreSnapshotAction row={row} />}
      {canManage && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label={row.killSwitch ? 'Resume pushes' : 'Pause pushes'}
          title={row.killSwitch ? 'Resume pushes' : 'Pause pushes'}
          disabled={isStandard || toggleKillSwitch.isPending}
          onClick={() => toggleKillSwitch.mutate()}
        >
          {row.killSwitch ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
        </Button>
      )}
      {canManage && (
        <Switch
          aria-label="Auto-run"
          checked={row.enabled}
          disabled={isStandard || setAutoRun.isPending}
          className="h-5 w-9 [&>span]:h-4 [&>span]:w-4 [&>span]:data-[state=checked]:translate-x-4"
          onCheckedChange={(next) =>
            next ? setConfirm('enable') : setAutoRun.mutate(false)
          }
        />
      )}
      {canManage && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label="Edit optimizer"
          title="Edit optimizer"
          onClick={() => onEdit(row)}
        >
          <Pencil className="h-4 w-4" />
        </Button>
      )}
      {canManage && (
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive"
          aria-label="Remove config"
          title="Remove config"
          onClick={() => setConfirm('remove')}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
      {confirm && dialogs[confirm]}
    </div>
  );
};
